import { offsetLabel, timezoneAbbreviation } from "@visioni/a3s-monitoring/app";

/** What every Patriot tools field shows when it has no value. */
export const EMPTY = "—";

const DATE_TIME: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false };

/**
 * An activation instant with seconds and its zone — support compares these
 * against Patriot's own screens and device logs, so the zone has to be on the
 * page rather than assumed.
 *
 * In `timeZone` where a list is filtered in one, so the rows read in the same
 * wall clock as the range that chose them, named as the timezone picker names
 * it: `AEDT` where the zone has an abbreviation, else its offset (`UTC+8`).
 * Otherwise in the viewer's own zone, with its offset.
 */
export function formatInstant(iso: string | undefined, timeZone?: string): string {
  return formatWith(DATE_TIME, iso, timeZone);
}

/**
 * `formatInstant` with milliseconds, for the Patriot times stored finer than a
 * second: signal `receivedDateTime` and note `noteDateTime`. Patriot keeps them
 * in 1/300 s ticks, so the last digit is good to about ±2 ms. Activation times
 * stay on `formatInstant`, as `respondedDateTime` is almost always whole seconds.
 */
export function formatPreciseInstant(iso: string | undefined, timeZone?: string): string {
  return formatWith({ ...DATE_TIME, fractionalSecondDigits: 3 }, iso, timeZone);
}

function formatWith(options: Intl.DateTimeFormatOptions, iso: string | undefined, timeZone?: string): string {
  if (iso === undefined || iso === "") return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  if (timeZone === undefined) return date.toLocaleString("en-AU", { ...options, timeZoneName: "shortOffset" });
  const zone = timezoneAbbreviation(timeZone, date) ?? offsetLabel(timeZone, date);
  const local = date.toLocaleString("en-AU", { ...options, timeZone });
  return zone === undefined ? local : `${local} ${zone}`;
}

/**
 * A `YYYY-MM-DD` calendar date as the date fields show it, `DD/MM/YYYY`. Only
 * the digits move: the date is already in the zone it was counted in.
 */
export function formatDay(day: string): string {
  const [year, month, date] = day.split("-");
  return year === undefined || month === undefined || date === undefined ? day : `${date}/${month}/${year}`;
}

/**
 * From one instant to another, in the viewer's zone, for something like an
 * activation that may still be open. The end drops its date when it falls on
 * the same day as the start.
 */
export function formatSpan(from: string, to: string | undefined): string {
  const start = formatInstant(from);
  if (to === undefined) return `${start} – still open`;
  const [startDate, endDate] = [new Date(from), new Date(to)];
  const sameDay = !Number.isNaN(endDate.getTime()) && startDate.toDateString() === endDate.toDateString();
  return `${start} – ${sameDay ? endDate.toLocaleTimeString("en-AU", TIME) : formatInstant(to)}`;
}

/**
 * Whether a Patriot text field has anything to show. The API keeps an empty
 * field as `""` rather than dropping it, so present is not the same as shown.
 */
export function hasText(text: string | undefined): text is string {
  return text !== undefined && text !== "";
}

/** A Patriot text field for display, or `EMPTY` when it has nothing to show. */
export function formatText(text: string | undefined): string {
  return hasText(text) ? text : EMPTY;
}
